import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { FaCalendar, FaCopy } from "react-icons/fa";
import TrainerContext from "../../../../../context/trainerContext/TrainerContext";
import { isAccessAllowed } from "../../../../../utils/helpers";
import DataTableBase from "../../../common/Datatable";
import DateRangeComponent from "../../../common/DateRangeComponent";
import makeRequest from "../../../../../api";
import { slotsService, trainerService } from "../../../../../api/services";

const statusList = ["ALL", "UPCOMING", "MISSED", "COMPLETED", "CANCELLED"];
const pollTime = 1000 * 60 * 5; // 5 min

const TrainerSessionsListing = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const currentTrainerId = queryParams.get("trainerId") || undefined;

  const date = moment();

  const { getTrainerState } = useContext(TrainerContext);
  const trainerData = getTrainerState();

  const [currentDate, setCurrentDate] = useState(date);
  const [sessionsList, setSessionsList] = useState<any>([]);
  const [filteredSessionsList, setFilteredSessionsList] = useState<any>([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [trainerDetails, setTrainerDetails] = useState<any>(null);

  const startDate = moment(currentDate)
    .startOf("week")
    .add(1, "day")
    .format("YYYY-MM-DD");
  const endDate = moment(currentDate)
    .endOf("week")
    .add(1, "day")
    .format("YYYY-MM-DD");
  const { trainerId, trainerIsAdmin } = trainerData;
  const trainerIdForData =
    currentTrainerId && trainerIsAdmin ? currentTrainerId : trainerId;

  const isMealPlanViewAllowed = isAccessAllowed("MEAL_PLAN_VIEW");
  const isNotesViewAllowed = isAccessAllowed("NOTES_VIEW");
  const isUserSessionsViewAllowed = isAccessAllowed("USER_SESSIONS_VIEW");

  useEffect(() => {
    let interval: any;
    const visibilityChangeEventListener = () => {
      if (document.visibilityState === "visible") {
        getSessionsList();
        setStatusFilter("ALL");
        interval = setInterval(() => {
          getSessionsList();
          setStatusFilter("ALL");
        }, pollTime);
      } else {
        if (interval) {
          clearInterval(interval);
        }
      }
    };
    visibilityChangeEventListener();
    document.addEventListener(
      "visibilitychange",
      visibilityChangeEventListener
    );
    return () => {
      document.removeEventListener(
        "visibilitychange",
        visibilityChangeEventListener
      );
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [currentDate]);

  useEffect(() => {
    let filteredList = sessionsList;
    if (statusFilter !== "ALL") {
      filteredList = sessionsList?.map((item: any) => ({
        ...item,
        sessions: item?.sessions?.filter(
          (item: any) => item?.status === statusFilter
        ),
      }));
    }

    const data = filteredList?.filter(
      (item: any) => item?.sessions?.length > 0
    );

    setFilteredSessionsList([...data]);
  }, [sessionsList, statusFilter]);

  useEffect(() => {
    if (!!currentTrainerId) {
      getTrainerDetails(currentTrainerId);
    }
  }, [currentTrainerId]);

  const getTrainerDetails = async (currentTrainerId: any) => {
    const res = await makeRequest(
      trainerService.getTrainerData(
        { trainerId: currentTrainerId },
        currentTrainerId
      )
    );
    setTrainerDetails({ ...res?.data?.trainer });
  };

  const getSessionsList = async () => {
    try {
      const response = await makeRequest(
        slotsService.getTrainerSessionsList(
          {
            trainerId: trainerIdForData,
            startDate,
            endDate,
          },
          trainerIdForData
        )
      );

      if (response?.status === 200) {
        const res = response?.data?.body?.sort(
          (a: any, b: any) => +a?.startTime - +b?.startTime
        );
        const dayWiseSessions = Array(
          moment(endDate).diff(startDate, "days") + 1
        )
          .fill({ date: startDate })
          .map((item, index) => {
            const arr: any = [];
            return {
              date: moment(startDate).add(index, "day").format("YYYY-MM-DD"),
              sessions: arr,
            };
          });

        res?.forEach((item: any) => {
          const date = moment(item?.startTime * 1000).format("YYYY-MM-DD");
          const dateDiff = moment(date).diff(startDate, "days");
          if (dateDiff >= 0 && dateDiff <= 6) {
            dayWiseSessions[dateDiff].sessions = [
              ...dayWiseSessions[dateDiff].sessions,
              {
                ...item,
                formattedStartTime: moment(item?.startTime * 1000).format(
                  "hh:mm A"
                ),
                formattedEndTime: moment(item?.endTime * 1000).format(
                  "hh:mm A"
                ),
              },
            ];
          }
        });

        setSessionsList([...dayWiseSessions]);
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message}`);
    }
  };

  const markAttendance = async (
    isAttended: boolean,
    userId: string,
    sk: string
  ) => {
    try {
      const response = await makeRequest(
        slotsService.markUserAttendence(
          { userId, sk, isAttended },
          trainerIdForData
        )
      );
      if (response?.status === 200) {
        toast.success("Attendance marked successfully!");
        getSessionsList();
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message}`);
    }
  };

  const handleAttendanceButtonClick = (e: any) => {
    const attendanceStatus = e.target.getAttribute("data-value");
    const sk = e.target.getAttribute("data-sk");
    const userId = e.target.getAttribute("data-userid");

    if (
      window.confirm(
        "Please confirm your decision to mark the attendance, as this action cannot be changed."
      )
    ) {
      markAttendance(attendanceStatus, userId, sk);
    }
  };

  const handleChangeCurrentDate = (action: string) => {
    if (action === "add") {
      setCurrentDate(moment(currentDate).add("1", "week"));
    } else {
      setCurrentDate(moment(currentDate).subtract("1", "week"));
    }
  };

  const handleStatusButtonClick = (e: any) => {
    const status = e.target.getAttribute("data-status");
    setStatusFilter(status);
  };

  const handleCopyToClipboard = (str: string) => {
    navigator.clipboard.writeText(str);
    toast.success("Copied to clipboard!");
  };

  const columns = [
    {
      name: "User name",
      selector: (row: any) => row.userName,
      cell: (row: any) => {
        return <div>{row?.userName}</div>;
      },
      width: "16%",
    },
    {
      name: "Time",
      selector: (row: any) => row?.startTime,
      cell: (row: any) => {
        return (
          <div>
            {`${moment(row?.startTime * 1000).format("hh:mm A")} - 
            ${moment(row?.endTime * 1000).format("hh:mm A")}`}
          </div>
        );
      },
      width: "17%",
    },
    {
      name: "Actions",
      selector: (row: any) => row.zoomMeetingLink,
      cell: (row: any) => {
        return (
          <div className="">
            <Link
              to={row?.zoomMeetingLink}
              target="_blank"
              className="btn btn-sm btn-outline-primary px-2 me-2"
            >
              Join
            </Link>
            <span
              className="cursor-pointer"
              onClick={() => {
                handleCopyToClipboard(row?.zoomMeetingLink);
              }}
            >
              <FaCopy />
            </span>
          </div>
        );
      },
      width: "10%",
    },
    {
      name: "User ID",
      selector: (row: any) => row.userId,
      cell: (row: any) => {
        return (
          <div className="d-flex align-items-center gap-1">
            <span>{row?.userId?.substring(0, 8)}...</span>
            <span
              className="cursor-pointer"
              onClick={() => {
                handleCopyToClipboard(row?.userId);
              }}
            >
              <FaCopy />
            </span>
          </div>
        );
      },
      width: "12.5%",
    },
    {
      name: "Status",
      selector: (row: any) => row.status,
      cell: (row: any) => {
        return (
          <div
            className={`text-capitalize ${
              row?.status === "MISSED" || row?.status === "CANCELLED"
                ? "text-danger"
                : row?.status === "RESCHEDULED"
                ? "text-secondary"
                : "text-success"
            }`}
          >
            {row?.status?.toLowerCase()}
          </div>
        );
      },
      width: "11%",
    },
    {
      name: "Attendance",
      selector: (row: any) => row.isAttended,
      cell: (row: any) => {
        const isSessionDone =
          row.status === "MISSED" ||
          row.status === "ONGOING" ||
          row.status === "RECENTLY_ENDED";

        const isAttended = row.isAttended;
        const isAttendanceMarked = isAttended === true || isAttended === false;

        return (
          <div className="d-flex">
            <button
              disabled={!isSessionDone || isAttendanceMarked}
              className={`btn btn-sm px-2 me-2 ${
                isAttended === true ? "btn-dark" : "btn-outline-dark"
              }`}
              data-value={true}
              data-userid={row.userId}
              data-sk={row.sk}
              onClick={handleAttendanceButtonClick}
            >
              Yes
            </button>
            <button
              disabled={!isSessionDone || isAttendanceMarked}
              className={`btn btn-sm px-2 me-2 ${
                isAttended === false ? "btn-dark " : "btn-outline-dark"
              }`}
              data-value={false}
              data-userid={row.userId}
              data-sk={row.sk}
              onClick={handleAttendanceButtonClick}
            >
              No
            </button>
          </div>
        );
      },
      width: "12%",
    },
    {
      name: "View",
      selector: (row: any) => row,
      cell: (row: any) => {
        return (
          <div className="d-flex gap-1">
            {isMealPlanViewAllowed && (
              <Link
                to={`/users/${row?.userId}/meal-plan`}
                state={row}
                className="btn btn-sm btn-outline-success"
                style={{
                  textDecoration: "none",
                }}
              >
                Plans
              </Link>
            )}
            {isNotesViewAllowed && (
              <Link
                to={`/users/${row?.userId}/notes`}
                state={row}
                style={{
                  textDecoration: "none",
                }}
                className="btn btn-sm btn-outline-primary"
              >
                Notes
              </Link>
            )}
            {isUserSessionsViewAllowed && (
              <Link
                to={`/users/${row?.userId}/sessions`}
                state={row}
                style={{
                  textDecoration: "none",
                }}
                className="btn btn-sm btn-outline-dark"
              >
                Sessions
              </Link>
            )}
          </div>
        );
      },
      width: "21.5%",
    },
  ];

  return (
    <div className="container-lg">
      <h6 className="fw-700 fs-24 mb-2">
        {trainerIsAdmin ? trainerDetails?.name : "Your"} Sessions
      </h6>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center justify-content-start">
          {statusList?.map((item: string, index: number) => (
            <button
              key={`status-button-${index}`}
              className={`btn btn-sm rounded-pill me-3 px-4 text-capitalize ${
                item === statusFilter
                  ? "btn-outline-success fw-600"
                  : "btn-outline-secondary"
              }`}
              data-status={item}
              onClick={handleStatusButtonClick}
            >
              {item.toLowerCase()}
            </button>
          ))}
        </div>
        <DateRangeComponent
          handleDateChange={handleChangeCurrentDate}
          startDate={startDate}
          endDate={endDate}
        />
      </div>
      {filteredSessionsList?.length === 0 ? (
        <h6 className="mb-0">No sessions for the week.</h6>
      ) : (
        filteredSessionsList?.map((item: any, index: number) => (
          <div className="mb-4" key={`daywise-session-list-${index}`}>
            <div className="d-flex align-items-center fs-18 fw-600 mb-2">
              <FaCalendar className="me-2" />
              {moment(item?.date).format("dddd, DD MMM YY")}
            </div>
            <DataTableBase
              columns={columns}
              data={item?.sessions}
              pagination={false}
            />
          </div>
        ))
      )}
    </div>
  );
};

export default TrainerSessionsListing;
